import
{
    Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import
{
    ExecutionResult,
    IDefaultEmail,
} from "@tylertech/tyler-api-types";

import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";

import { IReqDefaultEmailInfo } from "../../interfaces/IReqDefaultEmailInfo";
import { ModuleControlService } from "../../services";
import { DefaultEmailService } from "@tylertech/core-module";

@Component({
    selector: "po-notice-emails",
    templateUrl: "./poNoticeEmails.tmpl.html",
    styleUrls: [
        "./poNoticeEmails.component.scss"
    ],
    standalone: false
})
export class PoNoticeEmailsComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();

    readonly formGroup: FormGroup;
    readonly dialogTitle = "Send PO Notice Emails";

    defaultEmailBody: string;
    defaultEmailSubject: string;

    @Input() emailBody: string;
    @Output() emailBodyChange = new EventEmitter<string>();

    @Input() emailSubject: string;
    @Output() emailSubjectChange = new EventEmitter<string>();

    @Input() emails: IReqDefaultEmailInfo[];
    @Input() requisitionIds: string[];

    @Input() sendEmails: boolean;
    @Output() sendEmailsChange = new EventEmitter<boolean>();

    isLoaded: boolean;

    constructor(
        private $defaultEmailService: DefaultEmailService,
        private $purchasingModuleControlService: ModuleControlService,
    )
    {
        this.formGroup = new FormGroup({
            subject: LgdFormControlFactory.text(undefined, false, {}),
            message: LgdFormControlFactory.textarea(
                undefined,
                false,
                {
                    rows: 5,
                }),
            sendEmails: LgdFormControlFactory.checkbox(),
        });
    }

    private get subjectControl(): AbstractControl<string> { return this.formGroup.get("subject"); }
    private get messageControl(): AbstractControl<string> { return this.formGroup.get("message"); }
    private get sendEmailsControl(): AbstractControl<boolean> { return this.formGroup.get("sendEmails"); }

    get hasEmails(): boolean { return this.emails?.length > 0; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        Promise.all(
            [
                this.$defaultEmailService.GetEmailList(this.requisitionIds)
                    .then((emailsFromReq: IDefaultEmail[]) =>
                    {
                        this.emails
                            .forEach(
                                reqDetail =>
                                {
                                    reqDetail.Emails = emailsFromReq.filter(defaultEmails => defaultEmails.EntityId === reqDetail.Id);
                                });

                        this.emails = this.emails.filter(details => details.Emails.length > 0);
                    }),
                this.$purchasingModuleControlService.GetSettings()
                    .then((executionResult: ExecutionResult<Record<string, string>>) =>
                    {
                        this.defaultEmailSubject = executionResult.Result["POModuleControlEmailSettings.Subject"];
                        this.defaultEmailBody = executionResult.Result["POModuleControlEmailSettings.Message"];

                        setEnabled(this.subjectControl, true)
                            .setValue(this.defaultEmailSubject);
                        setEnabled(this.messageControl, true)
                            .setValue(this.defaultEmailBody);

                        this.sendEmailsControl.setValue(true);
                    })
            ])
            .finally(
                () =>
                {
                    this.isLoaded = true;
                });

        setEnabled(this.subjectControl, false)
            .setValue(this.emailSubject);
        subscribeToValueChanges(this.subjectControl, (newValue: string) =>
        {
            this.emailSubjectChange.emit(newValue);

            this.reloadDefaultEmailSettings();
        });

        setEnabled(this.messageControl, false)
            .setValue(this.emailBody);
        subscribeToValueChanges(this.messageControl, (newValue: string) =>
        {
            this.emailBodyChange.emit(newValue);

            this.reloadDefaultEmailSettings();
        });

        setEnabled(this.sendEmailsControl, false)
            .setValue(this.sendEmails);
        subscribeToValueChanges(this.sendEmailsControl, (newValue: boolean) =>
        {
            this.sendEmailsChange.emit(newValue);
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private reloadDefaultEmailSettings(): void
    {
        if (!this.emailSubject)
        {
            this.emailSubject = this.defaultEmailSubject;
        }

        if (!this.emailBody)
        {
            this.emailBody = this.defaultEmailBody;
        }
    }

    getEmailGroupClass(isEven: boolean): Record<string, boolean>
    {
        return { even: isEven };
    }
}
